// Vendor-allocation helpers for the Bulk Purchase Order (PO) tab's Allocate
// panel. Mirrors the spirit of purchase-return-calculations.js's
// batch/document grouping, but PO items have no batch/expiry/tax concept —
// just a quantity that can be split across one or more vendors.

import { toNumber } from "./purchase-return-calculations";

// A row with no vendor_allocations yet behaves as if it has exactly one
// implicit allocation (its current flat cmr_id/cmr_code/cmr_first_name +
// item_quantity) — so callers never need to special-case "not yet allocated".
export const getRowAllocations = (row) => {
  const allocations = row?.vendor_allocations || [];
  if (allocations.length > 0) return allocations;
  if (!row?.cmr_id && !row?.cmr_first_name) return [];
  return [
    {
      key: `${row?.item_id}-alloc-0`,
      cmr_id: row?.cmr_id,
      cmr_code: row?.cmr_code,
      cmr_name: row?.cmr_first_name,
      cmr_ship_to_address: null,
      qty: toNumber(row?.item_quantity),
      is_preference: true,
    },
  ];
};

// One "document" = one (vendor, ship-to address) pair the footer counts as
// a distinct purchase order. Mirrors buildCmrAllocationPayload's grouping
// (purchase-return-calculations.js) minus the pay-to component — POs have
// no pay-to address.
export const buildPoVendorAllocationGroups = (rows = []) => {
  const groups = new Map();

  rows.forEach((row) => {
    getRowAllocations(row).forEach((alloc) => {
      if (toNumber(alloc.qty) <= 0) return;
      const shipId = alloc.cmr_ship_to_address?.cmr_ship_address_id || "";
      const key = `${alloc.cmr_id || ""}__${shipId}`;

      if (!groups.has(key)) {
        groups.set(key, {
          cmr_id: alloc.cmr_id,
          cmr_code: alloc.cmr_code,
          cmr_name: alloc.cmr_name,
          cmr_ship_to_address: alloc.cmr_ship_to_address || null,
          items: [],
        });
      }

      groups.get(key).items.push({
        item_id: row.item_id,
        item_code: row.item_code,
        item_quantity: toNumber(alloc.qty),
      });
    });
  });

  return Array.from(groups.values());
};

// Splits an already-flattened, fully-detailed bulk-create itemData array
// (one entry per row×vendor allocation, as built by handleSubmit) into one
// group per (vendor, ship-to address) — the same key buildPoVendorAllocationGroups
// uses for the footer's Documents count, so the number of groups here always
// matches what the footer shows. Each group becomes exactly one
// bulkPurchaseCreateAction call in the "Adding X out of N" create loop.
export const groupItemDataByVendorShip = (itemData = []) => {
  const groups = new Map();

  itemData.forEach((item) => {
    const shipId = item?.cmr_ship_to_address?.cmr_ship_address_id || "";
    const key = `${item?.cmr_id || ""}__${shipId}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });

  return Array.from(groups.values());
};

// required/allocated/remaining for the panel's stats bar — same shape as
// purchase-return-calculations.js's getRowAllocationSummary but without the
// batch/total_allocatable_qty concept, which PO doesn't have.
export const getPoRowAllocationSummary = (row) => {
  const required = toNumber(row?.item_quantity);
  const allocated = getRowAllocations(row).reduce((sum, alloc) => sum + toNumber(alloc.qty), 0);
  const remaining = Math.max(required - allocated, 0);

  let status = "not_allocated";
  if (allocated > 0 && allocated < required) status = "partial";
  else if (required > 0 && allocated >= required) status = "full";

  return { required, allocated, remaining, status };
};

// ---------------------------------------------------------------------------
// UOM helpers shared by the Bulk PO screen and its table.
// ---------------------------------------------------------------------------

// How many BASE units one unit of the given uom_dropdown entry represents.
// This is the item's pack size: a captured load-min-items row carries
// alt_uom "Bag pack of 10" with base_quantity "10.00" and base_uom "Piece",
// i.e. one bag is 10 pieces.
// Raw base_quantity is deliberate — it is exactly what the regular purchase
// order screen stores as selected_uom_qty (purchase-items-table.js maps
// `qty: uom?.base_quantity` onto its options and writes `sel?.qty` on a UOM
// change) and what every base-unit comparison then multiplies by
// (`selected_uom_qty * quantity`). purchase-return-calculations.js's
// calculateBatchAllocation uses the same `qty * base_quantity` rule. `qty` is
// accepted as an alias so an already-flattened option works too.
// Falls back to 1 — "this UOM already IS the base unit" — whenever no usable
// pack size is present, so callers can convert unconditionally and an item
// with no pack maths behaves exactly as it does today.
export const getUomBaseFactor = (uom) => {
  const base = toNumber(uom?.base_quantity ?? uom?.qty);
  return base > 0 ? base : 1;
};

// Turns a requirement expressed in BASE units into the number of whole `uom`
// packs that covers it. Rounds UP on purpose: a reorder suggestion must never
// be rounded below the item's minimum, and a sub-pack requirement must never
// collapse to "order nothing", which the submit guard rejects anyway. The
// quantity input on both PO screens refuses "." so whole packs are the only
// values the grid can hold.
export const convertBaseQtyToUom = (baseQty, uom) => {
  const qty = toNumber(baseQty);
  if (qty <= 0) return 0;
  return Math.max(1, Math.ceil(qty / getUomBaseFactor(uom)));
};

// The free-text pack description the "add PO" endpoint carries as
// item_baby_box_packing (e.g. "1 * Piece"). Same expression that used to be
// inlined in bulk-purchase-order.js's buildRowFromItem, so its output is
// unchanged; the base_uom_name fallback only matters for an already-flattened
// dropdown option, which has no nested base_uom object.
export const buildUomPackDescription = (uom) =>
  uom
    ? `${parseInt(uom?.alt_quantity, 10) || 1} * ${
        uom?.base_uom?.name || uom?.base_uom_name || uom?.alt_uom || ""
      }`.trim()
    : "";
